import { useEffect, useState } from "react";
import { Plus, Trash2, X, Loader2 } from "lucide-react";
import api from "@/lib/api";

const Mono = ({ children, className = "" }) => (
  <span className={`font-mono uppercase tracking-[0.2em] ${className}`}>{children}</span>
);

const STATUSES = ["planning", "in_progress", "on_hold", "complete"];
const STATUS_CLS = {
  planning: "bg-white/10 text-white/70 border-white/20",
  in_progress: "bg-gold-500/15 text-gold-300 border-gold-500/40",
  on_hold: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  complete: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
};
const EMPTY = { name: "", client: "", description: "", status: "planning", progress: 0, due_date: "", budget: 0, spent: 0, forecast: 0, contingency: 0, funding_source: "" };

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const num = (v) => Number(v) || 0;
  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/projects", {
        ...form, progress: num(form.progress), budget: num(form.budget),
        spent: num(form.spent), forecast: num(form.forecast), contingency: num(form.contingency),
      });
      setOpen(false); setForm(EMPTY); load();
    } finally { setSaving(false); }
  };
  const update = async (id, patch) => { await api.patch(`/projects/${id}`, patch); load(); };
  const remove = async (id) => { await api.delete(`/projects/${id}`); load(); };

  return (
    <div className="space-y-8 text-white" data-testid="projects-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <Mono className="text-gold-400 text-[0.65rem]">Sheet 04 — Delivery</Mono>
          <h1 className="font-heading font-light text-4xl tracking-tight mt-3">Projects</h1>
          <p className="text-white/55 mt-3 font-light">Track delivery and budgets. Financials here feed the Portfolio dashboard.</p>
        </div>
        <button onClick={() => setOpen(true)} data-testid="new-project-btn" className="bg-gold-500 text-navy-950 font-semibold px-6 py-3 rounded-sm hover:bg-gold-400 transition-colors flex items-center gap-2">
          <Plus className="h-4 w-4" /> New project
        </button>
      </div>

      {projects.length === 0 ? (
        <div className="border border-dashed border-gold-500/25 p-16 text-center text-white/45 font-light">
          No projects yet. Create your first to start tracking delivery and budgets.
        </div>
      ) : (
        <div className="border border-gold-500/25 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gold-500/20 bg-navy-900/60 text-left">
                {["Project", "Client", "Status", "Progress", "Budget", "Due", ""].map((h) => (
                  <th key={h} className="px-5 py-4"><Mono className="text-white/40 text-[0.5rem]">{h}</Mono></th>
                ))}
              </tr>
            </thead>
            <tbody>
              {projects.map((p) => (
                <tr key={p.id} className="border-b border-gold-500/10" data-testid={`project-row-${p.id}`}>
                  <td className="px-5 py-4">
                    <div className="text-white">{p.name}</div>
                    {p.description && <div className="text-white/40 text-xs mt-0.5 max-w-xs truncate">{p.description}</div>}
                  </td>
                  <td className="px-5 py-4 text-white/60">{p.client || "—"}</td>
                  <td className="px-5 py-4">
                    <select
                      value={p.status}
                      onChange={(e) => update(p.id, { status: e.target.value })}
                      data-testid={`project-status-${p.id}`}
                      className={`text-xs font-mono uppercase tracking-wider border px-2 py-1.5 bg-transparent ${STATUS_CLS[p.status] || ""}`}
                    >
                      {STATUSES.map((s) => <option key={s} value={s} className="bg-navy-900">{s.replace("_", " ")}</option>)}
                    </select>
                  </td>
                  <td className="px-5 py-4 w-40">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-navy-950 border border-gold-500/20 overflow-hidden">
                        <div className="h-full bg-gold-500" style={{ width: `${p.progress}%` }} />
                      </div>
                      <span className="font-mono text-xs text-white/60 w-9">{p.progress}%</span>
                    </div>
                  </td>
                  <td className="px-5 py-4 font-mono text-white/70">{p.budget ? "$" + Number(p.budget).toLocaleString() : "—"}</td>
                  <td className="px-5 py-4 text-white/60 font-mono text-xs">{p.due_date || "—"}</td>
                  <td className="px-5 py-4 text-right">
                    <button onClick={() => remove(p.id)} data-testid={`delete-project-${p.id}`} className="text-white/30 hover:text-red-400">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-navy-950/80 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={create} className="bg-navy-900 border border-gold-500/30 w-full max-w-lg p-8 space-y-5 max-h-[90vh] overflow-y-auto" data-testid="project-modal">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl text-white">New project</h2>
              <button type="button" onClick={() => setOpen(false)}><X className="h-5 w-5 text-white/60" /></button>
            </div>
            <PField label="Project name" value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} testid="project-name" />
            <PField label="Client" value={form.client} onChange={(v) => setForm((f) => ({ ...f, client: v }))} required={false} testid="project-client" />
            <div>
              <Mono className="text-white/60 text-[0.55rem] block mb-2">Description</Mono>
              <textarea rows={2} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} data-testid="project-description" className="w-full bg-navy-950 border border-gold-500/20 px-4 py-3 text-white focus:outline-none focus:border-gold-500 resize-none" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Mono className="text-white/60 text-[0.55rem] block mb-2">Status</Mono>
                <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))} className="w-full bg-navy-950 border border-gold-500/20 px-3 py-3 text-white focus:outline-none focus:border-gold-500">
                  {STATUSES.map((s) => <option key={s} value={s} className="bg-navy-900">{s.replace("_", " ")}</option>)}
                </select>
              </div>
              <div>
                <Mono className="text-white/60 text-[0.55rem] block mb-2">Progress %</Mono>
                <input type="number" min="0" max="100" value={form.progress} onChange={(e) => setForm((f) => ({ ...f, progress: e.target.value }))} className="w-full bg-navy-950 border border-gold-500/20 px-3 py-3 text-white focus:outline-none focus:border-gold-500" />
              </div>
              <div>
                <Mono className="text-white/60 text-[0.55rem] block mb-2">Due date</Mono>
                <input type="date" value={form.due_date} onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))} className="w-full bg-navy-950 border border-gold-500/20 px-3 py-3 text-white focus:outline-none focus:border-gold-500" />
              </div>
            </div>
            <Mono className="text-gold-400 text-[0.55rem] block pt-2">Portfolio financials</Mono>
            <div className="grid grid-cols-2 gap-4">
              <NumField label="Budget ($)" value={form.budget} onChange={(v) => setForm((f) => ({ ...f, budget: v }))} testid="project-budget" />
              <NumField label="Spent to date ($)" value={form.spent} onChange={(v) => setForm((f) => ({ ...f, spent: v }))} testid="project-spent" />
              <NumField label="Forecast at completion ($)" value={form.forecast} onChange={(v) => setForm((f) => ({ ...f, forecast: v }))} testid="project-forecast" />
              <NumField label="Contingency ($)" value={form.contingency} onChange={(v) => setForm((f) => ({ ...f, contingency: v }))} testid="project-contingency" />
            </div>
            <PField label="Funding source" value={form.funding_source} onChange={(v) => setForm((f) => ({ ...f, funding_source: v }))} required={false} testid="project-funding" />
            <button type="submit" disabled={saving} data-testid="save-project-btn" className="w-full bg-gold-500 text-navy-950 font-semibold py-3.5 rounded-sm hover:bg-gold-400 transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Create project
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

function PField({ label, value, onChange, testid, required = true }) {
  return (
    <div>
      <Mono className="text-white/60 text-[0.55rem] block mb-2">{label}</Mono>
      <input required={required} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full bg-navy-950 border border-gold-500/20 px-4 py-3 text-white focus:outline-none focus:border-gold-500" />
    </div>
  );
}
function NumField({ label, value, onChange, testid }) {
  return (
    <div>
      <Mono className="text-white/60 text-[0.55rem] block mb-2">{label}</Mono>
      <input type="number" min="0" step="1000" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full bg-navy-950 border border-gold-500/20 px-3 py-3 text-white focus:outline-none focus:border-gold-500" />
    </div>
  );
}
